import { useMutation } from '@apollo/client';
import { Modal } from '@blueprint/ui';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { CardTitle } from '@zoopla-eng/zds-components/headings';
import { ChevronDownMedium, LockSmall } from '@zoopla-eng/zds-components/icons';
import { InputField } from '@zoopla-eng/zds-components/input-field';
import { Link } from '@zoopla-eng/zds-components/link';
import { Bulleted } from '@zoopla-eng/zds-components/lists/bulleted';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { CREATE_SAVED_SEARCH } from '@zoopla/listing-search-queries';
import triggerTracking from '@blueprint/tracking/dist/Tracking';
import {
    getForSaleFilters,
    getPolyenc,
    getQ,
    getRadius,
    getSearchPath,
    getSearchType,
    getToRentFilters,
    IconContainer,
    SelectWrapper,
    StyledLabel,
    StyledSelect
} from '@zoopla/listing-search/filters/modal';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import time from './assets/time.svg';
import {
    StyledSearchPath,
    StyledSmallLabels
} from './SaveThisSearchModal.styles';
import {
    formatAdded,
    formatAvailableFrom,
    formatBedrooms,
    formatBillsIncluded,
    formatBuyerIncentive,
    formatChainFree,
    formatFeature,
    formatFurnishedState,
    formatHomeType,
    formatIncludeRented,
    formatIncludeSharedAccommodation,
    formatIncludeSold,
    formatKeywords,
    formatLocation,
    formatPetsAllowed,
    formatPrice,
    formatPropertyTypes,
    formatRadius,
    formatReducedPriceOnly,
    formatRentalTerm,
    formatSearchPath,
    formatSearchType
} from './utils';

enum ModalState {
    INITIAL = 'initial',
    SUCCESS = 'success',
    ERROR = 'error'
}

export type SaveThisSearchModalProps = {
    defaultAlertFrequency?: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
};

export const SaveThisSearchModal = ({
    defaultAlertFrequency = '',
    isOpen,
    onOpenChange
}: SaveThisSearchModalProps) => {
    const [modalState, setModalState] = useState<
        ModalState.INITIAL | ModalState.SUCCESS | ModalState.ERROR
    >(ModalState.INITIAL);

    const { query, pathname } = useRouter();

    const polyenc = getPolyenc(query);
    const q = getQ(query);
    const radius = getRadius(query);
    const searchPath = getSearchPath(query);
    const searchType = getSearchType(pathname);

    const formattedRadius = formatRadius(radius);
    const formattedSearchPath = formatSearchPath(searchPath);

    const forSaleFilters = getForSaleFilters(query, pathname);
    const toRentFilters = getToRentFilters(query);

    const allFilters = {
        ...forSaleFilters,
        ...toRentFilters
    };

    const {
        added,
        available_from: availableFrom,
        beds_max: bedsMax,
        beds_min: bedsMin,
        bills_included: billsIncluded,
        buyer_incentive: buyerIncentive,
        chain_free: chainFree,
        feature,
        furnished_state: furnishedState,
        include_rented: includeRented,
        include_shared_accommodation: includeSharedAccommodation,
        include_sold: includeSold,
        is_auction: isAuction,
        is_retirement_home: isRetirementHome,
        is_shared_ownership: isSharedOwnership,
        keywords,
        new_homes: newHomes,
        pets_allowed: petsAllowed,
        price_frequency: priceFrequency,
        price_max: priceMax,
        price_min: priceMin,
        property_sub_type: propertySubType,
        property_type: propertyType,
        reduced_price_only: reducedPriceOnly,
        rental_term: rentalTerm
    } = allFilters;

    const formattedFilters = [
        formatAdded(added),
        formatAvailableFrom(availableFrom),
        formatBedrooms(bedsMax, bedsMin),
        formatBillsIncluded(billsIncluded),
        formatBuyerIncentive(buyerIncentive),
        formatChainFree(chainFree),
        formatFeature(feature),
        formatFurnishedState(furnishedState),
        ...formatHomeType(
            isAuction,
            isRetirementHome,
            isSharedOwnership,
            newHomes
        ),
        formatIncludeRented(includeRented),
        formatIncludeSharedAccommodation(includeSharedAccommodation),
        formatIncludeSold(includeSold),
        formatKeywords(keywords),
        formatPetsAllowed(petsAllowed),
        formatPrice(priceMax, priceMin),
        formatPropertyTypes(propertySubType),
        formatReducedPriceOnly(reducedPriceOnly),
        formatRentalTerm(rentalTerm)
    ];

    const formattedSearchType = formatSearchType(searchType);

    const [createSavedSearchMutation] = useMutation(CREATE_SAVED_SEARCH, {
        onCompleted: (data) => {
            if (data.createNewSavedSearch.status === 200) {
                triggerTracking({
                    event: 'GAEvent',
                    eventCategory: 'search results',
                    eventAction: 'success',
                    eventLabel:
                        defaultAlertFrequency === ''
                            ? 'save_search'
                            : 'create_alert'
                });

                setModalState(ModalState.SUCCESS);
            } else {
                setModalState(ModalState.ERROR);
            }
        },
        onError: (e) => {
            setModalState(ModalState.ERROR);
        }
    });

    const [searchName, setSearchName] = useState(q ? q : formattedSearchPath);
    const [alertFrequency, setAlertFrequency] = useState(defaultAlertFrequency);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        setAlertFrequency(defaultAlertFrequency);
    }, [defaultAlertFrequency]);

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        createSavedSearchMutation({
            variables: {
                input: {
                    added,
                    availableFrom,
                    bedsMax,
                    bedsMin,
                    billsIncluded,
                    buyerIncentive,
                    chainFree,
                    description: searchName,
                    feature,
                    frequency: alertFrequency,
                    furnishedState,
                    identifier: formatLocation(searchPath),
                    includeRented,
                    includeRetirementHomes:
                        isRetirementHome !== 'false' ? 'true' : 'false',
                    includeSharedAccommodation: includeSharedAccommodation
                        ? includeSharedAccommodation
                        : 'true',
                    includeSharedOwnership:
                        isSharedOwnership !== 'false' ? 'true' : 'false',
                    includeSold,
                    isAuction,
                    isRetirementHome,
                    isSharedOwnership,
                    keywords,
                    newHomes,
                    petsAllowed,
                    polyenc,
                    priceFrequency: priceFrequency
                        ? priceFrequency
                        : 'per_month',
                    priceMax,
                    priceMin,
                    propertySubType,
                    propertyType:
                        propertyType === 'property' ? '' : propertyType,
                    q: q ? q : formatLocation(searchPath),
                    radius: radius ? radius : '0',
                    reducedPriceOnly,
                    rentalTerm,
                    section: searchType,
                    userAlertId: ''
                }
            }
        });
    };

    return (
        <Modal
            isShowing={isOpen}
            close={() => {
                setModalState(ModalState.INITIAL);

                onOpenChange(false);
            }}
            modalTitle="Save search and email alert"
        >
            <>
                {modalState === ModalState.INITIAL ? (
                    <form onSubmit={onSubmit}>
                        <Box pt="$M100">
                            <Stack>
                                <Box display="flex" justifyContent="center">
                                    <img
                                        alt=""
                                        src={time}
                                        width="120px"
                                        height="120px"
                                    />
                                </Box>
                                <CardTitle>
                                    {`Save your search and create an alert ${formattedSearchType} ${formattedRadius}`}{' '}
                                    <StyledSearchPath>
                                        {formattedSearchPath}
                                    </StyledSearchPath>
                                </CardTitle>
                                <Bulleted>
                                    {formattedFilters.map((formattedFilter) =>
                                        formattedFilter ? (
                                            <li key={formattedFilter}>
                                                {formattedFilter}
                                            </li>
                                        ) : null
                                    )}
                                </Bulleted>
                                <Divider direction="horizontal" />
                                <InputField
                                    name="name-your-search"
                                    label="Name your search"
                                    value={searchName}
                                    onChange={(e) => {
                                        setSearchName(e.target.value);
                                    }}
                                />
                                <SelectWrapper>
                                    <StyledLabel htmlFor="alert_frequency">
                                        How often do you like to receive alerts?
                                    </StyledLabel>
                                    <StyledSelect
                                        name="alert_frequency"
                                        id="alert_frequency"
                                        onChange={(e) => {
                                            setAlertFrequency(e.target.value);
                                        }}
                                        value={alertFrequency}
                                    >
                                        {[
                                            {
                                                value: '0',
                                                text: 'Instant property alerts'
                                            },
                                            {
                                                value: '1',
                                                text: 'Daily summary emails'
                                            },
                                            {
                                                value: '3',
                                                text: 'Summary every 3 days'
                                            },
                                            {
                                                value: '7',
                                                text: 'Weekly summary emails'
                                            },
                                            {
                                                value: '',
                                                text: 'No email alerts'
                                            }
                                        ].map((option, index) => {
                                            return (
                                                <option
                                                    value={option.value}
                                                    key={`alert_frequency_${index}`}
                                                >
                                                    {option.text}
                                                </option>
                                            );
                                        })}
                                    </StyledSelect>
                                    <IconContainer>
                                        <ChevronDownMedium />
                                    </IconContainer>
                                </SelectWrapper>
                                <Button
                                    category="primary"
                                    disabled={!searchName}
                                >
                                    Save your search and alert
                                </Button>
                            </Stack>
                        </Box>
                    </form>
                ) : null}
                {modalState === ModalState.SUCCESS ? (
                    <Box pt="$M100">
                        <Stack>
                            <CardTitle>
                                {`Success! Your search "${searchName}" has been saved`}
                            </CardTitle>
                            <p>
                                You can access your saved searches from MyZoopla
                                account.
                            </p>
                            <Button
                                category="primary"
                                onClick={() => {
                                    setModalState(ModalState.INITIAL);

                                    onOpenChange(false);
                                }}
                            >
                                Return to search results
                            </Button>
                            <Box display="flex" justifyContent="center">
                                <Link href="/myaccount/alerts-searches/">
                                    Manage my saved searches
                                </Link>
                            </Box>
                            <Box
                                display="flex"
                                columnGap="$S200"
                                alignItems="center"
                            >
                                <Box width="16px" height="16px">
                                    <LockSmall />
                                </Box>
                                <StyledSmallLabels>
                                    We will not share your details with third
                                    parties
                                </StyledSmallLabels>
                            </Box>
                        </Stack>
                    </Box>
                ) : null}
                {modalState === ModalState.ERROR ? null : null}
            </>
        </Modal>
    );
};
